import { useState, useCallback } from 'react';

type AudioMode = 'muted' | 'unmuted';

interface AudioState {
  mode: AudioMode;
  isActive: boolean;
  isLoading: boolean;
  error: string | null;
}

interface UseEZOPENAudioProps {
  cameraId: number;
  cloudSerialNo?: string;
  cloudChannelNo?: string;
  cloudProvider?: string;
}

/**
 * Simplified Hook - Listen-only audio from camera
 * Handles:
 * - Audio mode toggle (off/listen)
 * - Event dispatching for EZOPENPlayer
 */
export const useEZOPENAudio = ({
  cameraId,
  cloudSerialNo,
  cloudChannelNo,
  cloudProvider
}: UseEZOPENAudioProps) => {
  const [state, setState] = useState<AudioState>({
    mode: 'muted',
    isLoading: false,
    isActive: false,
    error: null
  });

  // Check if camera supports audio
  const isAudioSupported = Boolean(
    cloudProvider && cloudSerialNo && cloudChannelNo
  );

  /**
   * Unmute camera audio
   */
  const unmuteAudio = useCallback(() => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    // Dispatch event for EZOPENPlayer to enable audio
    window.dispatchEvent(new CustomEvent('ezopen-audio-enable', {
      detail: { cameraId, volume: 100 }
    }));

    // Wait for success/failure from EZOPENPlayer
    const handleSuccess = () => {
      setState(prev => ({
        ...prev,
        mode: 'unmuted',
        isActive: true,
        isLoading: false
      }));
      cleanup();
    };

    const handleFailure = (event: Event) => {
      const customEvent = event as CustomEvent;
      const errorMsg = customEvent.detail?.error || 'Failed to unmute audio';
      setState(prev => ({
        ...prev,
        mode: 'muted',
        isActive: false,
        isLoading: false,
        error: errorMsg
      }));
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('ezopen-audio-success', handleSuccess);
      window.removeEventListener('ezopen-audio-failed', handleFailure);
    };

    window.addEventListener('ezopen-audio-success', handleSuccess);
    window.addEventListener('ezopen-audio-failed', handleFailure);

    // Timeout after 10 seconds
    setTimeout(() => {
      if (state.isLoading) {
        handleFailure(new CustomEvent('ezopen-audio-failed', {
          detail: { error: 'Audio initialization timeout' }
        }));
      }
    }, 10000);
  }, [cameraId, state.isLoading]);

  /**
   * Mute audio
   */
  const muteAudio = useCallback(() => {
    console.log('🔇 Muting audio...');

    // Dispatch event to disable audio in player
    window.dispatchEvent(new CustomEvent('ezopen-audio-disable', {
      detail: { cameraId }
    }));

    setState({
      mode: 'muted',
      isActive: false,
      isLoading: false,
      error: null
    });

    console.log('✅ Audio muted');
  }, [cameraId]);

  /**
   * Toggle between muted and unmuted modes
   */
  const cycleAudioMode = useCallback(() => {
    if (state.mode === 'muted') {
      unmuteAudio();
    } else {
      muteAudio();
    }
  }, [state.mode, unmuteAudio, muteAudio]);

  return {
    // State
    audioMode: state.mode,
    isAudioActive: state.isActive,
    isAudioLoading: state.isLoading,
    audioError: state.error,
    isAudioSupported,
    
    // Always 0 for listen-only
    audioLevel: 0,
    isMuted: state.mode === 'muted',

    // Actions
    cycleAudioMode,
    muteAudio
  };
};
